import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  enviarSolicitudAmistad,
  cancelarAmistad,
  getPublicProfile,
} from "../services/api";
import countriesData from "../data/countries";
import { useWebSocketActions } from "../context/WebSocketContext";
import { UserRound, UserMinus, Clock3 } from "lucide-react";


const getCountryFlag = (countryCode) => {
  if (typeof countryCode !== "string") return "";

  const code = countryCode.trim().toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) return "";

  return String.fromCodePoint(
    ...Array.from(code).map(
      (letter) => 127397 + letter.charCodeAt(0)
    )
  );
};

const ProfileCard = ({ profile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [friendStatus, setFriendStatus] = useState(profile.friendship_status);
  const [processing, setProcessing] = useState(false);
  const { addListener } = useWebSocketActions();

  useEffect(() => {
    setFriendStatus(profile.friendship_status);
  }, [profile.friendship_status]);

  useEffect(() => {
    const remove = addListener((data) => {
      if (data.type !== "notification") return;

      const { type, sender_id } = data.data;

      if (String(sender_id) !== String(profile.id)) return;

      switch (type) {
        case "friend_request_accepted":
          setFriendStatus("accepted");
          break;
        case "friend_request_rejected":
        case "friendship_cancelled":
          setFriendStatus("none");
          break;
        default:
          break;
      }
    });

    return () => remove();
  }, [profile.id, addListener]);

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;

    const hoy = new Date();
    const cumple = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - cumple.getFullYear();
    const m = hoy.getMonth() - cumple.getMonth();

    if (m < 0 || (m === 0 && hoy.getDate() < cumple.getDate())) edad--;

    return edad;
  };

  const getCountryLabel = (code) => {
    const found = countriesData.find((c) => c.countryCode === code);
    return found?.countryLabel || code;
  };

  const handleFollow = async () => {
    if (processing) return;

    setProcessing(true);

    try {
      if (friendStatus === "accepted") {
        await cancelarAmistad(profile.id);
        toast.success(t("friends.unfollow_success"));
      } else {
        await enviarSolicitudAmistad(profile.id);
        toast.success(t("search_disease.follow_success"));
        setFriendStatus("pending");
      }

      const updated = await getPublicProfile(profile.id);
      setFriendStatus(updated.data.friendship_status);
    } catch (err) {
      console.error("Error al modificar amistad:", err);
      toast.error(t("search_disease.friend_error"));
    } finally {
      setProcessing(false);
    }
  };

  const renderFollowButton = () => {
    if (friendStatus === "self") return null;

    if (friendStatus === "pending") {
      return (
        <button
          type="button"
          disabled
          className="w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-500"
        >
          {t("profile.request_pending")}
        </button>
      );
    }

    if (friendStatus === "received") {
      return (
        <button
          type="button"
          disabled
          className="w-full rounded-xl border border-[#B8D8EA] bg-[#EFF7FC] px-4 py-2.5 text-sm font-medium text-[#245580]"
        >
          {t("profile.request_received")}
        </button>
      );
    }

    if (friendStatus === "accepted") {
      return (
        <button
          type="button"
          disabled={processing}
          onClick={(e) => {
            e.stopPropagation();
            handleFollow();
          }}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
        >
          <UserMinus size={16} />
          {t("profile.unfollow")}
        </button>
      );
    }

    return (
      <button
        type="button"
        disabled={processing}
        onClick={(e) => {
          e.stopPropagation();
          handleFollow();
        }}
        className="flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[#247BA8] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1C638A] hover:shadow-md disabled:opacity-50"
      >
        <UserRound size={16} />
        {t("profile.send_request")}
      </button>
    );
  };

  const edad = calcularEdad(profile.date_of_birth);

  const ubicacion = [
    profile.ciudad,
    getCountryLabel(profile.country),
  ].filter(Boolean).join(", ");

  const caracteristicas = [
    ["habla", profile.habla, "🗣️"],
    ["escribe", profile.escribe, "✍️"],
    ["movilidad", profile.movilidad, "🚶"],
    ["motricidad", profile.motricidad, "🤲"],
    ["ver", profile.ver, "👁️"],
    ["audition", profile.audition, "👂"],
    ["memoire", profile.memoire, "🧠"],
    ["orientation", profile.orientation, "🧭"],
    ["concentration", profile.concentration, "🧩"],
  ];

  // HAPPYCAP_PROFILE_CARD_HARMONY_V2
  // HAPPYCAP_PROFILE_CARD_SOFT_SOCIAL_V1
  return (
    <article
      onClick={() => navigate(`/perfil/${profile.id}`)}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-[26px] border border-[#E3EDF4] bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[#B8D8EA] hover:shadow-lg"
    >
      {/* Cabecera suave y protagonista */}
      <div className="relative border-b border-[#E3EDF4] bg-gradient-to-br from-[#F1F8FC] via-[#F7FAFD] to-white px-5 pb-5 pt-6">
        <div className="flex min-w-0 items-start gap-4">
          <div className="shrink-0 rounded-[22px] bg-white p-1.5 shadow-sm ring-1 ring-[#E3EDF4]">
            <img
              src={profile.photo || "/default-avatar.png"}
              alt={t("profile.photo_alt", "Foto de perfil")}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/default-avatar.png";
              }}
              className="h-20 w-20 rounded-[17px] object-cover"
            />
          </div>

          <div className="min-w-0 flex-1 pt-1">
            <h3 className="break-words text-xl font-bold leading-tight tracking-tight text-[#245580] transition-colors group-hover:text-[#247BA8]">
              {profile.nombre} {profile.apellidos}
            </h3>

            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
              {ubicacion && (
                <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium leading-5 text-[#648098]">
                  <span className="shrink-0 text-sm" aria-hidden="true">
                    {getCountryFlag(profile.country) || "📍"}
                  </span>
                  <span className="break-words">{ubicacion}</span>
                </span>
              )}

              {edad !== null && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium leading-5 text-[#648098]">
                  <Clock3 size={14} className="text-[#247BA8]" />
                  {edad} {t("profile.years_old")}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Características: filas editoriales sin recuadros */}
      <div className="flex-1 px-5 py-3">
        <div className="divide-y divide-[#EDF2F6]">
          {caracteristicas.map(([clave, valor, icono]) => (
            <div
              key={clave}
              className="flex min-w-0 items-center justify-between gap-3 py-2.5"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center text-lg"
                  aria-hidden="true"
                >
                  {icono}
                </span>

                <span className="min-w-0 break-words text-[11px] font-semibold uppercase leading-4 tracking-wide text-[#648098]">
                  {t(`profile.${clave}`)
                    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, "")
                    .trim()}
                </span>
              </div>

              <span className="max-w-[52%] min-w-0 break-words text-right text-[13px] font-semibold leading-5 text-[#245580]">
                {valor
                  ? t(`profile_options.${valor}`, valor)
                  : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Acción principal */}
      <div
        className="mt-auto border-t border-[#EDF2F6] bg-[#FAFCFE] px-5 py-4"
        onClick={(e) => e.stopPropagation()}
      >
        {renderFollowButton()}
      </div>
    </article>
  );
};

export default ProfileCard;
